// Domain types — keep in sync with supabase/migrations/0001_initial_schema.sql

export type Uuid = string;
export type Iso8601 = string;
export type IsoDate = string; // YYYY-MM-DD

export type SubscriptionTier = 'free' | 'smart' | 'pro' | 'enterprise';
export type Language = 'en' | 'tn';
export type AccountType = 'asset' | 'liability' | 'equity' | 'income' | 'expense';
export type JournalSource =
  | 'receipt'
  | 'sale'
  | 'purchase'
  | 'manual'
  | 'bank'
  | 'payroll'
  | 'adjustment';
export type OcrStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'manual';
export type OcrTier = 0 | 1 | 2 | 3; // 0=manual, 1=template, 2=flash, 3=pro
export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'partial' | 'overdue' | 'cancelled';
export type ReminderType = 'vat' | 'paye' | 'income_tax' | 'salary' | 'supplier' | 'custom';
export type ReminderStatus = 'pending' | 'done' | 'dismissed' | 'overdue';

export interface Profile {
  id: Uuid;
  phone: string | null;
  email: string | null;
  display_name: string | null;
  preferred_language: Language;
  timezone: string;
  is_admin: boolean;
  must_change_password: boolean;
  /** Public URL of the practitioner's profile picture. Optional. */
  avatar_url: string | null;
  created_at: Iso8601;
  updated_at: Iso8601;
}

export interface Business {
  id: Uuid;
  owner_id: Uuid;
  name: string;
  trading_name: string | null;
  registration_no: string | null;
  vat_number: string | null;
  vat_registered: boolean;
  industry: string | null;
  fiscal_year_start_month: number;
  base_currency: string;
  tier: SubscriptionTier;
  tier_renews_at: Iso8601 | null;
  is_demo: boolean;
  invoice_seq: number;
  address: string | null;
  phone: string | null;
  /** Public URL of the clinic / shopfront logo. Optional. */
  logo_url: string | null;
  created_at: Iso8601;
  updated_at: Iso8601;
}

export interface Account {
  id: Uuid;
  business_id: Uuid;
  gl_code: string;
  name: string;
  type: AccountType;
  parent_id: Uuid | null;
  is_active: boolean;
  is_system: boolean;
  created_at: Iso8601;
}

export interface JournalEntry {
  id: Uuid;
  business_id: Uuid;
  entry_date: IsoDate;
  reference: string | null;
  description: string | null;
  source: JournalSource;
  source_id: Uuid | null;
  is_posted: boolean;
  is_locked: boolean;
  posted_at: Iso8601 | null;
  created_by: Uuid | null;
  created_at: Iso8601;
}

export interface JournalLine {
  id: Uuid;
  entry_id: Uuid;
  account_id: Uuid;
  debit: number;
  credit: number;
  vat_amount: number;
  vat_rate: number;
  description: string | null;
}

export interface Receipt {
  id: Uuid;
  business_id: Uuid;
  image_url: string;
  image_hash: string | null;
  ocr_status: OcrStatus;
  ocr_tier_used: OcrTier | null;
  ocr_confidence: number | null;
  ocr_raw_response: unknown;
  merchant_name: string | null;
  receipt_date: IsoDate | null;
  total_amount: number | null;
  vat_amount: number | null;
  vat_rate: number | null;
  line_items: LineItem[] | null;
  category_suggested: string | null;
  account_id: Uuid | null;
  journal_entry_id: Uuid | null;
  uploaded_by: Uuid | null;
  uploaded_at: Iso8601;
}

export interface LineItem {
  description: string;
  quantity: number;
  unit_price: number;
  line_total: number;
}

export interface OcrResponse {
  merchant_name: string;
  receipt_date: IsoDate | null;
  receipt_time: string | null;
  total_amount: number | null;
  subtotal: number | null;
  vat_amount: number | null;
  vat_rate: number | null;
  currency: string;
  line_items: LineItem[];
  payment_method: 'cash' | 'card' | 'transfer' | 'mobile_money' | 'unknown';
  confidence: number;
  issues: string[];
  raw_text: string;
}

export interface Invoice {
  id: Uuid;
  business_id: Uuid;
  invoice_number: string;
  customer_name: string;
  customer_id: Uuid | null;
  issue_date: IsoDate;
  due_date: IsoDate | null;
  subtotal: number;
  vat_amount: number;
  total: number;
  status: InvoiceStatus;
  paid_amount: number;
  notes: string | null;
  created_at: Iso8601;
  updated_at: Iso8601;
}

export interface InventoryItem {
  id: Uuid;
  business_id: Uuid;
  sku: string | null;
  name: string;
  unit: string;
  cost_price: number | null;
  sale_price: number | null;
  qty_on_hand: number;
  reorder_point: number;
  is_active: boolean;
  created_at: Iso8601;
  updated_at: Iso8601;
}

export interface Reminder {
  id: Uuid;
  business_id: Uuid;
  type: ReminderType;
  title: string;
  amount: number | null;
  due_date: IsoDate;
  status: ReminderStatus;
  notify_via: string[];
  notified_at: Iso8601 | null;
  completed_at: Iso8601 | null;
  created_at: Iso8601;
}

// ───── Patient management (Healthcare practices only — DPA-regulated PHI) ─────

export type Gender = 'male' | 'female' | 'other' | 'prefer_not_to_say';
export type VisitType =
  | 'consultation'
  | 'follow_up'
  | 'procedure'
  | 'prescription'
  | 'dispense'
  | 'lab_request'
  | 'referral'
  | 'tele_consult';
export type PaymentStatus = 'unpaid' | 'paid' | 'medical_aid' | 'waived';
export type PatientAuditAction =
  | 'view_patient'
  | 'create_patient'
  | 'update_patient'
  | 'delete_patient'
  | 'view_visit'
  | 'create_visit'
  | 'update_visit'
  | 'lock_visit'
  | 'export_records'
  | 'dispense_drug'
  | 'view_dispense_history';

export interface Patient {
  id: Uuid;
  business_id: Uuid;
  first_name: string;
  last_name: string;
  date_of_birth: IsoDate | null;
  gender: Gender | null;
  national_id: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  next_of_kin_name: string | null;
  next_of_kin_phone: string | null;
  next_of_kin_relationship: string | null;
  medical_aid_scheme: string | null;
  medical_aid_member_number: string | null;
  blood_group: string | null;
  known_allergies: string | null;
  chronic_conditions: string | null;
  consent_to_store_records: boolean;
  consent_recorded_at: Iso8601 | null;
  registered_at: Iso8601;
  last_visit_at: Iso8601 | null;
  is_active: boolean;
  deleted_at: Iso8601 | null;
  created_by: Uuid | null;
  created_at: Iso8601;
  updated_at: Iso8601;
}

export interface PatientVitals {
  bp_systolic?: number;
  bp_diastolic?: number;
  pulse?: number;
  temp_c?: number;
  weight_kg?: number;
  height_cm?: number;
  spo2?: number;
  notes?: string;
}

export interface PatientVisit {
  id: Uuid;
  business_id: Uuid;
  patient_id: Uuid;
  visit_date: IsoDate;
  visit_type: VisitType;
  chief_complaint: string | null;
  vitals: PatientVitals;
  examination: string | null;
  diagnosis: string | null;
  icd10_codes: string[] | null;
  treatment_notes: string | null;
  follow_up_at: IsoDate | null;
  amount_charged: number;
  payment_status: PaymentStatus;
  invoice_id: Uuid | null;
  attended_by: Uuid | null;
  is_locked: boolean;
  locked_at: Iso8601 | null;
  attachments: Array<{ url: string; name: string; mime?: string; size?: number }>;
  deleted_at: Iso8601 | null;
  created_at: Iso8601;
  updated_at: Iso8601;
}

export interface PatientPrescription {
  id: Uuid;
  business_id: Uuid;
  visit_id: Uuid;
  patient_id: Uuid;
  drug_name: string;
  strength: string | null;
  form: string | null;
  dosage: string | null;
  frequency: string | null;
  duration: string | null;
  quantity: number | null;
  instructions: string | null;
  is_dispensed: boolean;
  dispensed_at: Iso8601 | null;
  dispensed_by: Uuid | null;
  created_at: Iso8601;
}

export interface PatientAuditEntry {
  id: Uuid;
  business_id: Uuid;
  patient_id: Uuid | null;
  visit_id: Uuid | null;
  actor_id: Uuid | null;
  action: PatientAuditAction;
  context: Record<string, unknown>;
  created_at: Iso8601;
}

/** Industry strings that gate the Patient Management module in the UI. */
export const HEALTHCARE_INDUSTRIES = [
  'Healthcare / Doctor',
  'Healthcare / Dentist',
  'Healthcare / Pharmacist',
  'Healthcare / Clinic or Small Hospital',
] as const;

export function isHealthcareBusiness(industry: string | null): boolean {
  if (!industry) return false;
  // Prefix match so seeded values like "Healthcare / Dental" still trip the
  // gate alongside the canonical Doctor / Dentist / Pharmacist / Clinic ones.
  return industry.toLowerCase().startsWith('healthcare');
}

// ───── Pulse Pharmacy (Pharmacist practices) — keep in sync with 0026 ─────

export type DrugForm =
  | 'tablet'
  | 'capsule'
  | 'syrup'
  | 'suspension'
  | 'injection'
  | 'cream'
  | 'ointment'
  | 'drops'
  | 'inhaler'
  | 'suppository'
  | 'powder'
  | 'other';

/** Botswana medicine schedule. OTC = over-the-counter; S1–S4 are controlled
 *  (habit-forming) and require a controlled-substances register entry. */
export type DrugSchedule = 'OTC' | 'S1' | 'S2' | 'S3' | 'S4';
export type VatTreatment = 'standard' | 'zero' | 'exempt';
export type DispensePaymentMethod = 'cash' | 'card' | 'mobile_money' | 'medical_aid' | 'account';
export type ControlledMovement =
  | 'receipt'
  | 'dispense'
  | 'adjustment'
  | 'destruction'
  | 'transfer_in'
  | 'transfer_out';
export type ClaimStatus = 'draft' | 'submitted' | 'part_paid' | 'paid' | 'rejected';
export type ClaimLineStatus = 'pending' | 'approved' | 'short_paid' | 'rejected';

export interface PharmacyDrug {
  id: Uuid;
  business_id: Uuid;
  name: string;
  generic_name: string | null;
  strength: string | null;
  form: DrugForm | null;
  atc_code: string | null;
  schedule: DrugSchedule;
  barcode: string | null;
  pack_size: number;
  dispense_unit: string;
  reorder_level: number;
  cost_price: number | null;
  sale_price: number | null;
  markup_pct: number | null;
  vat_treatment: VatTreatment;
  requires_prescription: boolean;
  notes: string | null;
  is_active: boolean;
  deleted_at: Iso8601 | null;
  created_by: Uuid | null;
  created_at: Iso8601;
  updated_at: Iso8601;
}

export interface PharmacyBatch {
  id: Uuid;
  business_id: Uuid;
  drug_id: Uuid;
  batch_no: string;
  expiry_date: IsoDate;
  qty_received: number;
  qty_on_hand: number;
  unit_cost: number;
  supplier_id: Uuid | null;
  received_at: Iso8601;
  received_by: Uuid | null;
  journal_entry_id: Uuid | null;
  deleted_at: Iso8601 | null;
  created_at: Iso8601;
  updated_at: Iso8601;
}

/** One FEFO allocation recorded on a dispense line (JSONB element). */
export interface BatchAllocation {
  batch_id: Uuid;
  batch_no: string;
  expiry_date: IsoDate;
  qty: number;
  unit_cost: number;
}

export interface PharmacyDispense {
  id: Uuid;
  business_id: Uuid;
  patient_id: Uuid | null;
  dispense_no: string;
  dispense_date: IsoDate;
  prescriber_name: string | null;
  prescription_ref: string | null;
  subtotal: number;
  vat_amount: number;
  total: number;
  payment_method: DispensePaymentMethod;
  payment_status: PaymentStatus;
  invoice_id: Uuid | null;
  journal_entry_id: Uuid | null;
  counselled: boolean;
  dispensed_by: Uuid | null;
  is_void: boolean;
  void_reason: string | null;
  voided_at: Iso8601 | null;
  deleted_at: Iso8601 | null;
  created_at: Iso8601;
  updated_at: Iso8601;
}

export interface PharmacyDispenseLine {
  id: Uuid;
  business_id: Uuid;
  dispense_id: Uuid;
  drug_id: Uuid;
  prescription_id: Uuid | null;
  qty: number;
  unit_price: number;
  line_total: number;
  vat_amount: number;
  unit_cost: number;
  batch_allocations: BatchAllocation[];
  directions: string | null;
  created_at: Iso8601;
}

export interface ControlledRegisterEntry {
  id: Uuid;
  business_id: Uuid;
  drug_id: Uuid;
  entry_date: IsoDate;
  movement: ControlledMovement;
  qty_in: number;
  qty_out: number;
  balance_after: number;
  batch_id: Uuid | null;
  dispense_id: Uuid | null;
  patient_id: Uuid | null;
  prescriber_name: string | null;
  reference: string | null;
  witness_name: string | null;
  recorded_by: Uuid | null;
  context: Record<string, unknown>;
  created_at: Iso8601;
}

export interface PharmacyClaim {
  id: Uuid;
  business_id: Uuid;
  scheme: string;
  claim_ref: string | null;
  period_start: IsoDate | null;
  period_end: IsoDate | null;
  status: ClaimStatus;
  total_claimed: number;
  total_paid: number;
  submitted_at: Iso8601 | null;
  paid_at: Iso8601 | null;
  journal_entry_id: Uuid | null;
  notes: string | null;
  deleted_at: Iso8601 | null;
  created_by: Uuid | null;
  created_at: Iso8601;
  updated_at: Iso8601;
}

export interface PharmacyClaimLine {
  id: Uuid;
  business_id: Uuid;
  claim_id: Uuid;
  dispense_id: Uuid;
  patient_id: Uuid | null;
  member_number: string | null;
  amount_claimed: number;
  amount_paid: number;
  status: ClaimLineStatus;
  rejection_reason: string | null;
  created_at: Iso8601;
}

/** Schedules that require a controlled-substances register entry. */
export const CONTROLLED_SCHEDULES = ['S1', 'S2', 'S3', 'S4'] as const;

export function isControlledSchedule(schedule: DrugSchedule): boolean {
  return (CONTROLLED_SCHEDULES as readonly string[]).includes(schedule);
}

/** Gates the Pulse Pharmacy module in the UI (pharmacist practices only). */
export function isPharmacyBusiness(industry: string | null): boolean {
  return !!industry && /pharma/i.test(industry);
}
